import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './SettleDebtModal.css';

function SettleDebtModal({ householdId, balance, onClose, onSuccess }) {
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('expenses'); // 'expenses' or 'amount'
  
  // Amount mode state
  const [amount, setAmount] = useState(Math.abs(balance.net).toFixed(2));
  const [notes, setNotes] = useState('');
  
  // Expenses mode state
  const [unpaidExpenses, setUnpaidExpenses] = useState([]);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(true);

  const isOweYou = balance.net > 0; // They owe you
  const isYouOwe = balance.net < 0; // You owe them
  const currentDebt = Math.abs(balance.net);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get current user ID
        const profileRes = await api.get('/auth/profile');
        setCurrentUserId(profileRes.data.id);
        
        // Get unpaid expenses
        const expensesRes = await api.get(
          `/household/${householdId}/unpaid-expenses/${balance.other_user_id}`
        );
        setUnpaidExpenses(expensesRes.data.expenses || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingExpenses(false);
      }
    };
    fetchData();
  }, [householdId, balance.other_user_id]);

  const handleExpenseToggle = (expense) => {
    const isSelected = selectedExpenses.find(e => e.split_id === expense.split_id);
    if (isSelected) {
      setSelectedExpenses(selectedExpenses.filter(e => e.split_id !== expense.split_id));
    } else {
      setSelectedExpenses([...selectedExpenses, expense]);
    }
  };

  const getSelectedTotal = () => {
    return selectedExpenses.reduce((sum, e) => sum + e.split_amount, 0);
  };

  const getExcessAmount = () => {
    const selected = getSelectedTotal();
    const excess = selected - currentDebt;
    return excess > 0 ? excess : 0;
  };

  const getActualSettlementAmount = () => {
    const selected = getSelectedTotal();
    return Math.min(selected, currentDebt);
  };

  const hasExcess = () => {
    return getSelectedTotal() > currentDebt;
  };

  const handleSubmitExpenses = async (e) => {
    e.preventDefault();
    if (!currentUserId) {
      alert('Unable to get current user information');
      return;
    }

    if (selectedExpenses.length === 0) {
      alert('Please select at least one expense to settle');
      return;
    }

    // Warn if selecting more than owed
    if (hasExcess()) {
      const confirmed = window.confirm(
        `You're selecting €${getSelectedTotal().toFixed(2)} but only €${currentDebt.toFixed(2)} is owed.\n\n` +
        `This will settle the full debt of €${currentDebt.toFixed(2)}.\n\n` +
        `Note: The selected expenses will still show €${getExcessAmount().toFixed(2)} as unpaid.\n\n` +
        `Continue?`
      );
      if (!confirmed) return;
    }

    setLoading(true);

    try {
      const settlementAmount = getActualSettlementAmount();
      
      // FIX: Only mark splits as paid - DO NOT also create a settlement record
      // Creating both was causing double-counting in the debt calculation
      // The is_paid flag on splits is the source of truth for expense-based settlements
      await api.post(`/household/${householdId}/mark-splits-paid`, {
        split_ids: selectedExpenses.map(e => e.split_id)
      });
      
      // NOTE: We no longer create a settlement record when marking specific expenses as paid
      // Settlements are only for lump-sum payments that don't correspond to specific expenses
      
      alert(`Settlement recorded! €${settlementAmount.toFixed(2)} marked as paid.`);
      onSuccess();
    } catch (error) {
      console.error('Error recording settlement:', error);
      alert('Failed to record settlement: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAmount = async (e) => {
    e.preventDefault();
    if (!currentUserId) {
      alert('Unable to get current user information');
      return;
    }

    const settlementAmount = parseFloat(amount);
    if (settlementAmount > currentDebt) {
      const confirmed = window.confirm(
        `You entered €${settlementAmount.toFixed(2)} but only €${currentDebt.toFixed(2)} is owed.\n\n` +
        `Continue with €${settlementAmount.toFixed(2)}?`
      );
      if (!confirmed) return;
    }

    setLoading(true);

    try {
      const settlementData = {
        from_user_id: isYouOwe ? currentUserId : balance.other_user_id,
        to_user_id: isYouOwe ? balance.other_user_id : currentUserId,
        amount: settlementAmount,
        currency: 'EUR',
        settlement_date: new Date().toISOString().split('T')[0],
        notes: notes || `Settlement payment`
      };

      await api.post(`/household/${householdId}/settle`, settlementData);
      
      alert(`Settlement recorded! €${settlementAmount.toFixed(2)} marked as paid.`);
      onSuccess();
    } catch (error) {
      console.error('Error recording settlement:', error);
      alert('Failed to record settlement: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Separate expenses by direction
  const expensesTheyOweYou = unpaidExpenses.filter(e => e.direction === 'they_owe_you');
  const expensesYouOweThem = unpaidExpenses.filter(e => e.direction === 'you_owe_them');
  const relevantExpenses = isOweYou ? expensesTheyOweYou : expensesYouOweThem;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content settle-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>💰 Settle Debt</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="settlement-info">
          {isOweYou && (
            <div className="settlement-notice positive">
              <p><strong>{balance.other_user_name}</strong> is paying you back</p>
              <p className="amount-due">Current debt: €{currentDebt.toFixed(2)}</p>
            </div>
          )}
          {isYouOwe && (
            <div className="settlement-notice negative">
              <p>You are paying <strong>{balance.other_user_name}</strong></p>
              <p className="amount-due">Current debt: €{currentDebt.toFixed(2)}</p>
            </div>
          )}
        </div>

        {/* Mode Tabs */}
        <div className="settlement-tabs">
          <button
            className={`tab ${mode === 'expenses' ? 'active' : ''}`}
            onClick={() => setMode('expenses')}
          >
            📋 Select Expenses
          </button>
          <button
            className={`tab ${mode === 'amount' ? 'active' : ''}`}
            onClick={() => setMode('amount')}
          >
            💵 Enter Amount
          </button>
        </div>

        {/* Expenses Mode */}
        {mode === 'expenses' && (
          <div className="expenses-mode">
            {loadingExpenses ? (
              <div className="loading">Loading expenses...</div>
            ) : relevantExpenses.length === 0 ? (
              <div className="no-expenses">
                <p>No unpaid expenses found</p>
                <p className="hint">Switch to "Enter Amount" to record a lump sum payment</p>
              </div>
            ) : (
              <>
                <div className="expenses-list">
                  {relevantExpenses.map(expense => (
                    <label key={expense.split_id} className="expense-item">
                      <input
                        type="checkbox"
                        checked={selectedExpenses.some(e => e.split_id === expense.split_id)}
                        onChange={() => handleExpenseToggle(expense)}
                      />
                      <div className="expense-details">
                        <div className="expense-header">
                          <span className="expense-desc">{expense.description}</span>
                          <span className="expense-amount">€{expense.split_amount.toFixed(2)}</span>
                        </div>
                        <div className="expense-meta">
                          <span className="expense-date">{formatDate(expense.date)}</span>
                          <span className="expense-category">{expense.category}</span>
                          <span className="expense-total">Total: €{expense.total_amount.toFixed(2)}</span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="selected-summary">
                  <div className="summary-row">
                    <span>Selected {selectedExpenses.length} expense{selectedExpenses.length !== 1 ? 's' : ''}</span>
                    <span className="selected-total">€{getSelectedTotal().toFixed(2)}</span>
                  </div>
                  
                  {hasExcess() && (
                    <>
                      <div className="summary-row warning">
                        <span>⚠️ Exceeds debt by</span>
                        <span className="excess-amount">€{getExcessAmount().toFixed(2)}</span>
                      </div>
                      <div className="summary-row actual">
                        <span>Actual settlement</span>
                        <span className="actual-amount">€{getActualSettlementAmount().toFixed(2)}</span>
                      </div>
                      <div className="excess-note">
                        💡 This will settle your full debt of €{getActualSettlementAmount().toFixed(2)}. The remaining €{getExcessAmount().toFixed(2)} will stay unpaid on the expense.
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={onClose} disabled={loading}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={handleSubmitExpenses}
                    disabled={loading || selectedExpenses.length === 0}
                  >
                    {loading ? 'Recording...' : 
                     hasExcess() ? `Settle Debt (€${getActualSettlementAmount().toFixed(2)})` :
                     `Settle Selected (€${getSelectedTotal().toFixed(2)})`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Amount Mode */}
        {mode === 'amount' && (
          <form onSubmit={handleSubmitAmount} className="amount-mode">
            <div className="form-group">
              <label>Settlement Amount (€) *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
              <small>Current debt: €{currentDebt.toFixed(2)}</small>
            </div>

            <div className="form-group">
              <label>Notes (Optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Paid via bank transfer, Cash payment, etc."
                rows="3"
              />
            </div>

            <div className="modal-actions">
              <button type="button" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="primary-btn" disabled={loading}>
                {loading ? 'Recording...' : 'Record Settlement'}
              </button>
            </div>
          </form>
        )}

        <div className="settlement-explanation">
          <p><strong>💡 Two ways to settle:</strong></p>
          <ul>
            <li><strong>Select Expenses:</strong> Choose specific expenses to mark as paid</li>
            <li><strong>Enter Amount:</strong> Record a lump sum payment (won't mark specific expenses)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default SettleDebtModal;
